import { ArrowRight, History, LayoutList, Target } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";

import { HistoryDrawer } from "@/components/HistoryDrawer";
import { SaveStatus } from "@/components/SaveStatus";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils/cn";
import { IssueIcon, thematicClasses } from "@/lib/thematic";
import { useAutosave } from "@/lib/hooks/useAutosave";

import {
  bulkSubmitAnswers,
  invalidateAfterAnswersSaved,
  useGoalActions,
  useGoalAnswers,
  useProgress,
  useQuestionnaireHistory,
  type BulkAnswerInput,
} from "../api";
import {
  ActionAnswerRow,
  type ActionAnswerDraft,
} from "../components/ActionAnswerRow";
import { ErrorBlock, LoadingBlock } from "../components/QueryState";
import {
  flattenGoals,
  type FlatGoal,
  isGoalComplete,
  thematicTotals,
} from "../progress-utils";
import { FocusMode } from "./FocusMode";
import type {
  AnswerType,
  FlushFn,
  QuestionnaireProgress,
  RegisterFlush,
} from "../types";

type DraftMap = Record<string, ActionAnswerDraft>;
type Mode = "browse" | "focus";

function draftsToInputs(drafts: DraftMap): BulkAnswerInput[] {
  return Object.entries(drafts)
    .filter(([, d]) => d.answer != null)
    .map(([action, d]) => ({
      action,
      answer: d.answer as AnswerType,
      comment: d.comment,
      show_comment_publicly: d.showCommentPublicly,
      priority: d.priority,
    }));
}

interface AnsweringStepProps {
  onContinue: () => void;
  registerFlush: RegisterFlush;
  /** Initial layout; the dashboard "continue" CTA opens focus mode. Defaults to browse. */
  initialMode?: Mode;
}

export function AnsweringStep({
  onContinue,
  registerFlush,
  initialMode,
}: AnsweringStepProps) {
  const { t } = useTranslation();
  const progress = useProgress();
  const [mode, setMode] = useState<Mode>(initialMode ?? "browse");
  const [historyOpen, setHistoryOpen] = useState(false);
  // Only fetched once opened — the feed is read-only and nothing else needs it.
  const history = useQuestionnaireHistory(historyOpen);

  // Register the active surface's autosave flush upward, and flush before a
  // mode switch so a pending debounced edit isn't dropped.
  const flushRef = useRef<FlushFn | null>(null);
  const register = useCallback(
    (fn: FlushFn | null) => {
      flushRef.current = fn;
      registerFlush(fn);
    },
    [registerFlush],
  );
  const switchMode = useCallback(async (next: Mode) => {
    await flushRef.current?.();
    setMode(next);
  }, []);

  if (progress.isLoading) return <LoadingBlock />;
  if (progress.error) return <ErrorBlock error={progress.error} />;

  const data = progress.data;
  if (!data || data.thematics.length === 0) {
    return (
      <Card>
        <CardContent className="space-y-3 py-8">
          {/* Shown here too, and here it matters most: a restricted account
              with nothing designated yet sees an empty questionnaire, and
              without this it has no way to tell that apart from a bug. */}
          <DemoContentNotice counts={data?.demo_content_counts} />
          <p className="text-sm text-muted-foreground">
            {t("questionnaire.answering.empty")}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      <DemoContentNotice counts={data.demo_content_counts} />
      <div className="flex flex-wrap items-center gap-4">
        <ModeSwitch mode={mode} onMode={(m) => void switchMode(m)} />
        <ProgressStrip data={data} className="min-w-[240px] flex-1" />
        {/* Offered to member and partner alike: reading who changed what needs
            only view consent, and the member needs it as much as the partner. */}
        <Button variant="outline" size="sm" onClick={() => setHistoryOpen(true)}>
          <History aria-hidden />
          {t("questionnaire.history.open")}
        </Button>
      </div>

      {historyOpen && (
        <HistoryDrawer
          title={t("questionnaire.history.title")}
          onClose={() => setHistoryOpen(false)}
          query={history}
        />
      )}

      {mode === "browse" ? (
        <BrowseMode data={data} onContinue={onContinue} registerFlush={register} />
      ) : (
        <FocusMode data={data} onContinue={onContinue} registerFlush={register} />
      )}
    </div>
  );
}

/**
 * How much of the Activateur a demonstration account is being shown.
 *
 * `null` for every other account, and both figures are computed server-side so
 * this page and the Django one print the same numbers rather than each counting
 * its own way.
 */
export function DemoContentNotice({
  counts,
}: {
  counts: QuestionnaireProgress["demo_content_counts"] | undefined;
}) {
  const { t } = useTranslation();
  if (!counts) return null;

  return (
    <p className="rounded-lg bg-muted px-3 py-2 text-xs text-muted-foreground">
      {t("questionnaire.answering.demoContentCounts", {
        visible: counts.visible,
        total: counts.total,
      })}
    </p>
  );
}

// --- Shared header pieces -----------------------------------------------------

function ModeSwitch({ mode, onMode }: { mode: Mode; onMode: (m: Mode) => void }) {
  const { t } = useTranslation();
  const item = (value: Mode, label: string, Icon: typeof LayoutList) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === value}
      onClick={() => onMode(value)}
      className={cn(
        "inline-flex items-center gap-2 rounded-md px-3.5 py-2 text-sm font-semibold transition-colors",
        mode === value
          ? "bg-background text-foreground shadow-sm"
          : "text-muted-foreground hover:text-foreground",
      )}
    >
      <Icon className="size-4" aria-hidden />
      {label}
    </button>
  );
  return (
    <div className="inline-flex gap-1 rounded-lg border bg-secondary p-1">
      {item("browse", t("questionnaire.answering.browse"), LayoutList)}
      {item("focus", t("questionnaire.answering.focus"), Target)}
    </div>
  );
}

/** Segmented overall-progress bar: one coloured slice per thematic answered. */
function ProgressStrip({
  data,
  className,
}: {
  data: QuestionnaireProgress;
  className?: string;
}) {
  const { t } = useTranslation();
  const totals = thematicTotals(data);
  const overall = data.total_actions || 1;
  return (
    <div className={cn("flex items-center gap-3", className)}>
      <div
        className="flex h-2.5 flex-1 overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-label={t("questionnaire.answering.progressLabel")}
        aria-valuenow={data.answered_actions}
        aria-valuemin={0}
        aria-valuemax={data.total_actions}
      >
        {totals.map((tt) => (
          <span
            key={tt.id}
            className={cn("h-full", thematicClasses(tt.cssClass).solid)}
            style={{ width: `${(tt.answered / overall) * 100}%` }}
          />
        ))}
      </div>
      <span className="text-sm font-bold tabular-nums text-foreground">
        {data.answered_actions} / {data.total_actions}
      </span>
    </div>
  );
}

// --- Browse mode --------------------------------------------------------------

function BrowseMode({
  data,
  onContinue,
  registerFlush,
}: {
  data: QuestionnaireProgress;
  onContinue: () => void;
  registerFlush: RegisterFlush;
}) {
  const { t } = useTranslation();
  const [goalId, setGoalId] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "unanswered">("all");

  const flushRef = useRef<FlushFn | null>(null);
  const register = useCallback(
    (fn: FlushFn | null) => {
      flushRef.current = fn;
      registerFlush(fn);
    },
    [registerFlush],
  );
  const selectGoal = useCallback(async (id: string) => {
    await flushRef.current?.();
    setGoalId(id);
  }, []);

  const goals = useMemo(() => flattenGoals(data), [data]);
  const firstGoalId = goals[0]?.goalId ?? null;
  useEffect(() => {
    if (goalId == null && firstGoalId) setGoalId(firstGoalId);
  }, [goalId, firstGoalId]);

  const currentGoal = goals.find((g) => g.goalId === goalId) ?? null;
  const currentIndex = goals.findIndex((g) => g.goalId === goalId);
  const nextGoal =
    currentIndex >= 0 && currentIndex < goals.length - 1
      ? goals[currentIndex + 1]
      : null;

  // A "next goal" button is always available while there is somewhere to
  // advance to (the next goal, or — on the last goal — the first still-incomplete
  // one so it's never a dead end), regardless of overall completion. "Voir les
  // résultats" is shown *in addition* once every action is answered, so a member
  // who has completed the questionnaire can still page goal-to-goal (previously
  // the results CTA replaced the next-goal button, hiding it entirely).
  // The completion flag, once set, never re-locks, so results stay reachable when
  // the member adds a new unanswered optional issue (C-4).
  const overallComplete =
    data.is_completed ||
    (data.total_actions > 0 && data.answered_actions >= data.total_actions);
  const firstIncompleteGoal =
    goals.find(
      (g) => !isGoalComplete({ total: g.total, answered: g.answered }),
    ) ?? null;
  const advanceTarget = nextGoal ?? firstIncompleteGoal;

  return (
    <div className="grid gap-6 lg:grid-cols-[19rem_1fr]">
      <GoalNav
        data={data}
        filter={filter}
        onFilter={setFilter}
        selectedGoalId={goalId}
        onSelect={(id) => void selectGoal(id)}
      />
      <div className="space-y-4">
        {currentGoal ? (
          <GoalPanel
            key={currentGoal.goalId}
            goal={currentGoal}
            registerFlush={register}
          />
        ) : (
          <p className="text-sm text-muted-foreground">
            {t("questionnaire.answering.selectGoal")}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          {advanceTarget && (
            <Button
              type="button"
              variant={overallComplete ? "outline" : "default"}
              onClick={() => void selectGoal(advanceTarget.goalId)}
            >
              {t("questionnaire.answering.nextGoal")}
              <ArrowRight aria-hidden />
            </Button>
          )}
          {overallComplete && (
            <Button type="button" onClick={onContinue}>
              {t("questionnaire.answering.viewResults")}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function GoalNav({
  data,
  filter,
  onFilter,
  selectedGoalId,
  onSelect,
}: {
  data: QuestionnaireProgress;
  filter: "all" | "unanswered";
  onFilter: (f: "all" | "unanswered") => void;
  selectedGoalId: string | null;
  onSelect: (id: string) => void;
}) {
  const { t } = useTranslation();

  const keepGoal = (g: { total_actions: number; answered_actions: number }) =>
    filter === "all" ||
    !isGoalComplete({ total: g.total_actions, answered: g.answered_actions });

  /**
   * Where a thematic or issue label sends you: its first unfinished objective,
   * or its first if they're all done. Those labels sat between the goal rows
   * looking exactly like them but doing nothing when clicked — every level of
   * the nav now goes somewhere.
   */
  const firstGoalId = (
    goals: { id: string; total_actions: number; answered_actions: number }[],
  ) =>
    (
      goals.find(
        (g) =>
          !isGoalComplete({ total: g.total_actions, answered: g.answered_actions }),
      ) ?? goals[0]
    )?.id;

  return (
    <nav
      aria-label={t("questionnaire.answering.navLabel")}
      className="self-start overflow-hidden rounded-xl border bg-card"
    >
      <div className="flex items-center gap-2 border-b p-2.5">
        <span className="ml-1 text-xs font-bold uppercase tracking-wide text-muted-foreground">
          {t("questionnaire.steps.issues")}
        </span>
        <div className="ml-auto inline-flex rounded-md bg-secondary p-0.5">
          {(["all", "unanswered"] as const).map((f) => (
            <button
              key={f}
              type="button"
              aria-pressed={filter === f}
              onClick={() => onFilter(f)}
              className={cn(
                "rounded px-2.5 py-1 text-xs font-semibold transition-colors",
                filter === f
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {f === "all"
                ? t("questionnaire.answering.filterAll")
                : t("questionnaire.answering.filterUnanswered")}
            </button>
          ))}
        </div>
      </div>

      <div className="max-h-[70vh] space-y-1 overflow-y-auto p-2">
        {data.thematics.map((thematic) => {
          const tc = thematicClasses(thematic.css_class);
          const issues = thematic.issues
            .map((issue) => ({
              ...issue,
              goals: issue.goals.filter(keepGoal),
            }))
            .filter((issue) => issue.goals.length > 0);
          if (issues.length === 0) return null;
          return (
            <div key={thematic.id} className="pt-1">
              <button
                type="button"
                onClick={() => {
                  const id = firstGoalId(issues.flatMap((i) => i.goals));
                  if (id) onSelect(id);
                }}
                className={cn(
                  "flex w-full items-center gap-2 rounded px-2 pb-1 pt-0.5 text-left text-xs font-bold uppercase tracking-wide hover:bg-accent",
                  tc.ink,
                )}
              >
                <span className={cn("size-2.5 rounded-sm", tc.solid)} aria-hidden />
                {thematic.name}
              </button>
              {issues.map((issue) => {
                // Each issue carries its own glyph in the questionnaire
                // catalogue — the same one the Django progress bar shows — so
                // the nav is scannable by symbol, not just by reading.
                return (
                <div key={issue.id} className="mb-1">
                  <button
                    type="button"
                    onClick={() => {
                      const id = firstGoalId(issue.goals);
                      if (id) onSelect(id);
                    }}
                    className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-[13px] font-semibold text-foreground hover:bg-accent"
                  >
                    <IssueIcon
                      icon={issue.icon}
                      className={cn("size-4 shrink-0", tc.ink)}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1 truncate">{issue.name}</span>
                  </button>
                  {/* Indented behind a rule and set in muted type: issue and
                      objective were both plain 14px at the same offset, so the
                      two levels read as one flat list. */}
                  <ul className="ml-3 space-y-0.5 border-l border-border pl-1.5">
                    {issue.goals.map((goal) => {
                      const done = isGoalComplete({
                        total: goal.total_actions,
                        answered: goal.answered_actions,
                      });
                      const active = goal.id === selectedGoalId;
                      return (
                        <li key={goal.id}>
                          <button
                            type="button"
                            onClick={() => onSelect(goal.id)}
                            aria-current={active}
                            className={cn(
                              "flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors",
                              active
                                ? cn("bg-background font-medium shadow-sm ring-1 ring-inset", tc.ring)
                                : "text-muted-foreground hover:bg-accent hover:text-foreground",
                            )}
                          >
                            <span className="truncate">{goal.name}</span>
                            <span
                              className={cn(
                                "shrink-0 rounded-full px-1.5 text-xs font-semibold tabular-nums",
                                done
                                  ? "bg-success text-success-foreground"
                                  : "bg-secondary text-muted-foreground",
                              )}
                            >
                              {goal.answered_actions}/{goal.total_actions}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </nav>
  );
}

// --- Goal answering panel -----------------------------------------------------

function GoalPanel({
  goal,
  registerFlush,
}: {
  goal: FlatGoal;
  registerFlush: RegisterFlush;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const goalId = goal.goalId;
  const actions = useGoalActions(goalId);
  const answers = useGoalAnswers(goalId);

  const [drafts, setDrafts] = useState<DraftMap>({});

  const autosave = useAutosave<DraftMap>({
    save: (draftMap, signal) =>
      bulkSubmitAnswers(goalId, draftsToInputs(draftMap), signal),
    shouldSave: (draftMap) => draftsToInputs(draftMap).length > 0,
    onSaved: () => invalidateAfterAnswersSaved(qc),
    onError: () => toast.error(t("questionnaire.saveError")),
  });

  useEffect(() => {
    registerFlush(autosave.flush);
    return () => registerFlush(null);
  }, [registerFlush, autosave.flush]);

  useEffect(() => {
    if (!answers.data) return;
    const seeded: DraftMap = {};
    for (const answer of answers.data) {
      if (!answer.action) continue;
      seeded[answer.action] = {
        answer: answer.answer,
        comment: answer.comment ?? "",
        showCommentPublicly: answer.show_comment_publicly ?? false,
        priority: answer.priority ?? null,
      };
    }
    setDrafts(seeded);
  }, [answers.data]);

  const update = (actionId: string, next: ActionAnswerDraft) => {
    setDrafts((prev) => {
      const merged = { ...prev, [actionId]: next };
      autosave.save(merged);
      return merged;
    });
  };

  if (actions.isLoading || answers.isLoading) return <LoadingBlock />;
  if (actions.error) return <ErrorBlock error={actions.error} />;
  if (answers.error) return <ErrorBlock error={answers.error} />;

  const list = actions.data ?? [];
  const tc = thematicClasses(goal.cssClass);
  const pct = goal.total > 0 ? Math.round((goal.answered / goal.total) * 100) : 0;

  return (
    <Card className="relative overflow-hidden">
      {/* Thematic accent — ties the answer panel to the pillar's identity. */}
      <span aria-hidden className={cn("absolute inset-y-0 left-0 w-1.5", tc.solid)} />
      <CardHeader className="pl-7">
        <p
          className={cn(
            "flex items-center gap-2 text-xs font-bold uppercase tracking-wide",
            tc.ink,
          )}
        >
          <span aria-hidden className={cn("size-2.5 rounded-sm", tc.solid)} />
          {goal.thematicName}
        </p>
        <p className="text-sm text-muted-foreground">{goal.issueName}</p>
        <CardTitle className="text-xl">{goal.goalName}</CardTitle>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <span className="flex h-1.5 w-40 overflow-hidden rounded-full bg-secondary sm:w-56">
            <span
              className={cn("h-full rounded-full", tc.solid)}
              style={{ width: `${pct}%` }}
            />
          </span>
          <span className={cn("text-xs font-semibold tabular-nums", tc.ink)}>
            {goal.answered}/{goal.total}
          </span>
          <SaveStatus status={autosave.status} className="ml-auto" />
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {list.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            {t("questionnaire.answering.noActions")}
          </p>
        ) : (
          list.map((action) => (
            <ActionAnswerRow
              key={action.id}
              action={action}
              draft={
                drafts[action.id] ?? {
                  answer: null,
                  comment: "",
                  showCommentPublicly: false,
                  priority: null,
                }
              }
              onChange={(next) => update(action.id, next)}
            />
          ))
        )}
      </CardContent>
    </Card>
  );
}
